import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private endpoint = 'https://graph.microsoft.com/v1.0/users';
  constructor(private http: HttpClient, private msalService: MsalService) { }

  private userDataSubject = new BehaviorSubject<any>(null); // Holds the userData
  userData$ = this.userDataSubject.asObservable(); // Expose it as observable

  getUserGroups(accessToken: string) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`
    });

    return this.http.get('https://graph.microsoft.com/v1.0/me/transitiveMemberOf', { headers });
  }

  getUserGroupsByTokenAndUserId(accessToken: string, userId : string ) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json"
    });
    const url = 'https://graph.microsoft.com/v1.0/users/'+ userId + '/getMemberObjects';
    const body = {
      "securityEnabledOnly": false
    };
    return this.http.post(url, body, { headers });
  }
  
  setUserData(data: any) {
    // console.log("set user");
    this.userDataSubject.next(data);
  }

  getUserData() {
    // console.log("get user");
    return this.userDataSubject.getValue();
  }

  async getUserById(id: string): Promise<GraphUser> {
    const token = await this.getAccessToken();
    const obs$ = this.http.get<GraphUser>(`${this.endpoint}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    // convert the single-value Observable into a Promise
    return firstValueFrom(obs$);
  }

  async getUsersBatch(ids: string[]): Promise<{ responses: any[] }> {
    const token = await this.getAccessToken();
    const batchRequests = ids.map((id, i) => ({
      id: `${i}`,
      method: 'GET',
      url: `/users/${id}`
    }));

    const obs$ = this.http.post<{ responses: any[] }>(
      'https://graph.microsoft.com/v1.0/$batch',
      { requests: batchRequests },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return firstValueFrom(obs$);
  }


  async getAccessToken(): Promise<string> {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        throw new Error("No active account found. Please login first.");
      }
  
      const tokenRequest = {
        scopes: ["https://graph.microsoft.com/.default"], // ✅ Ensures correct audience
        account: account
      };
  
      try {
        const response: AuthenticationResult = await this.msalService.instance.acquireTokenSilent(tokenRequest);
        return response.accessToken;
      } catch (error) {
        console.error("Error acquiring token:", error);
        throw error;
      }
    }  
}

export interface GraphUser {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  // …other fields you requested
}